import React from "react";
import { useTranslation } from "react-i18next";
import useAppNavigate from "../../hooks/useAppNavigate";
import { hand, connectBill } from "../../common/imagePath";
import {
  uploadBankStatement,
  uploadMore,
  uploadRecurringExpense,
} from "../../common/imagePath";
import HelpfulTipsCard from "../../components/widgets/HelpfulTipsCard";
import StepRow from "../../components/widgets/StepRow";
import HelpfulCard from "../../components/widgets/HelpfulCard";
import PageHeader from "../../components/widgets/PageHeader";
import { BlueTickIcon, EllipseDotIcon } from "../../components/ui/icons";

const ROW_HEIGHT = 40;
const INNER_PAD_TOP = 8;
const HAND_SIZE = 60;

const MORE_MENU_ITEMS = [
  { iconKey: "recurringExpense", labelKey: "addBill" },
  { iconKey: "connectBill",      labelKey: "connectBank" },
  { iconKey: "bankStatement",    labelKey: "uploadStatement", active: true },
  { iconKey: "more",             labelKey: "more" },
];

const UploadStatementMockup = () => {
  const { t } = useTranslation();

  const LABEL_MAP = {
    addBill:        t("uploadBankStatement.mockup.addBill"),
    connectBank:    t("uploadBankStatement.mockup.connectBank"),
    uploadStatement: t("uploadBankStatement.mockup.uploadStatement"),
    more:           t("uploadBankStatement.mockup.more"),
  };

  const ICON_MAP = {
    recurringExpense: uploadRecurringExpense,
    connectBill:      connectBill,
    bankStatement:    uploadBankStatement,
    more:             uploadMore,
  };

  const activeIndex = MORE_MENU_ITEMS.findIndex((item) => item.active);
  const handTop =
    INNER_PAD_TOP + activeIndex * ROW_HEIGHT + ROW_HEIGHT / 2 - HAND_SIZE / 2;

  return (
    <div className="mx-4 mb-6">
      <style>{`
        @keyframes handTap {
          0%   { transform: translateX(0)     scale(1);    }
          35%  { transform: translateX(-10px) scale(0.92); }
          55%  { transform: translateX(-6px)  scale(0.96); }
          100% { transform: translateX(0)     scale(1);    }
        }
      `}</style>
      <div className="border border-[#DEDEDE] dark:bg-[#1B1C1E] dark:border-[#1B1C1E] rounded-2xl p-6 relative overflow-visible">
        <div className="bg-white dark:bg-[#121212] border border-transparent dark:border-[#2A2F3A] rounded-xl px-2 py-2 relative overflow-visible min-w-[200px]">
          {MORE_MENU_ITEMS.map((item, index) => {
            const isActive = !!item.active;
            return (
              <React.Fragment key={index}>
                <div
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors duration-200 ${
                    index !== MORE_MENU_ITEMS.length - 1 ? "mb-0.5" : ""
                  } ${isActive ? "bg-[#3479F7]" : ""}`}
                >
                  <img
                    src={ICON_MAP[item.iconKey]}
                    alt=""
                    className={`h-4 object-contain flex-shrink-0 ${isActive ? "brightness-0 invert" : ""}`}
                  />
                  <span
                    className={`text-sm font-semibold ${
                      isActive ? "text-white" : "text-[#1C1C1E] dark:text-white"
                    }`}
                  >
                    {LABEL_MAP[item.labelKey]}
                  </span>
                </div>
                {index === 2 && (
                  <div className="mx-3 my-0.5 h-[0.5px] bg-[#DEDEDE] dark:bg-[#2A2F3A]" />
                )}
              </React.Fragment>
            );
          })}

          <img
            src={hand}
            alt=""
            className="absolute pointer-events-none select-none"
            style={{
              width: HAND_SIZE,
              height: HAND_SIZE,
              top: handTop,
              right: -26,
              animation: "handTap 1.4s ease-in-out infinite",
              transformOrigin: "60% 30%",
            }}
          />
        </div>
      </div>
    </div>
  );
};

const UploadBankStatement = () => {
  const navigate = useAppNavigate();
  const { t } = useTranslation();

  const overviewPoints = t("uploadBankStatement.overviewPoints", { returnObjects: true });
  const steps = t("uploadBankStatement.steps", { returnObjects: true });
  const tips = t("uploadBankStatement.tips", { returnObjects: true });

  return (
    <div className="bg-[#F2F2F7] dark:bg-[#000000] transition-colors duration-200 pb-4">
      <PageHeader
        title={t("uploadBankStatement.title")}
        onBack={() => navigate("/how-to-use")}
      />

      {/* Card 1 — Overview */}
      <div className="mx-4 mt-2 mb-4 rounded-2xl overflow-hidden border border-[#DEDEDE] dark:bg-[#1B1C1E] dark:border-[#1B1C1E]">
        <div className="px-4 pt-4 pb-4">
          <h2 className="font-inter font-semibold text-[18px] text-black dark:text-white mb-1">
            {t("uploadBankStatement.cardTitle")}
          </h2>
          <p className="text-xs lg:text-sm text-[#636363] dark:text-[#EBEBF5] dark:opacity-60 leading-relaxed mb-3 font-normal">
            {t("uploadBankStatement.cardDesc")}
          </p>
          <div className="flex flex-col gap-2">
            {overviewPoints.map((point, i) => (
              <div key={i} className="flex items-center gap-2">
                <BlueTickIcon />
                <span className="text-xs lg:text-sm font-normal text-black dark:text-white leading-snug">
                  {point}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Card 2 — App Mockup */}
      <UploadStatementMockup />

      {/* Section label */}
      <p className="px-4 mb-2 text-xs lg:text-sm font-semibold tracking-wide text-[#636363] uppercase">
        {t("uploadBankStatement.sectionLabel")}
      </p>

      {/* Card 3 — Steps */}
      <div className="mx-4 mb-4 rounded-2xl overflow-hidden bg-white border border-[#DEDEDE] dark:bg-[#1B1C1E] dark:border-[#1B1C1E]">
        <div className="px-4">
          {steps.map((step, i) => (
            <StepRow
              key={i}
              number={i + 1}
              title={step.title}
              desc={step.desc}
              items={step.items}
              isLast={i === steps.length - 1}
            />
          ))}
        </div>
      </div>

      {/* Card 4 — Helpful Tips */}
      <HelpfulTipsCard
        tips={tips}
        BulletComponent={EllipseDotIcon}
        itemAlign="items-center"
      />

      {/* Card 5 — Was this helpful? */}
      <HelpfulCard />
    </div>
  );
};

export default UploadBankStatement;
